import { anthropic } from "@ai-sdk/anthropic";
import { google } from "@ai-sdk/google";
import { openai } from "@ai-sdk/openai";
import { LanguageModel } from "ai";
import anthropicCatalog from "@/lib/ai/catalog/anthropic.json";
import googleCatalog from "@/lib/ai/catalog/google.json";
import openAICatalog from "@/lib/ai/catalog/openai.json";
import {
  CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES,
  isSupportedChatAttachmentMediaType,
} from "@/lib/ai/chat-attachments";

// `pnpm run catalog:update` で models.dev から取得・生成した JSON を src/lib/ai/catalog/ に配置し、ここへ列挙する
const PROVIDER_CATALOGS = {
  anthropic: anthropicCatalog,
  google: googleCatalog,
  openai: openAICatalog,
} as const;

type ProviderCatalogs = typeof PROVIDER_CATALOGS;
type RegisteredProviderId = keyof ProviderCatalogs;
export type ModelProviderId = RegisteredProviderId;

type CatalogModelId<P extends RegisteredProviderId> =
  P extends RegisteredProviderId
    ? keyof ProviderCatalogs[P]["models"] & string
    : never;

type ModelMetadata<P extends RegisteredProviderId> =
  ProviderCatalogs[P]["models"][CatalogModelId<P>] & {
    name?: string;
    limit?: { context?: number; output?: number };
    attachment?: boolean;
    modalities?: { input?: string[] };
    temperature: boolean;
  };

function defineFeaturedModel<P extends RegisteredProviderId>(
  providerId: P,
  modelId: CatalogModelId<P>,
) {
  return { providerId, modelId };
}

// UIで利用するラインアップ（順序はUI表示順）。追加プロバイダを入れる際はここへ追記する。
// 例: defineFeaturedModel("openai", "gpt-4o-mini"),
const FEATURED_MODELS = [
  defineFeaturedModel("anthropic", "claude-haiku-5-5"),
  defineFeaturedModel("anthropic", "claude-sonnet-5-5"),
  defineFeaturedModel("anthropic", "claude-opus-5-5"),
  defineFeaturedModel("google", "gemini-3.1-pro-preview"),
  defineFeaturedModel("google", "gemini-3.8-flash"),
  defineFeaturedModel("openai", "gpt-6-astra"),
  defineFeaturedModel("openai", "gpt-6-sol"),
  defineFeaturedModel("openai", "gpt-6-luna"),
] as const;

type FeaturedModelId = (typeof FEATURED_MODELS)[number]["modelId"];

type AvailableModel = {
  value: FeaturedModelId;
  label: string;
  provider: RegisteredProviderId;
};
function getModelMetadata<P extends RegisteredProviderId>(
  providerId: P,
  modelId: CatalogModelId<P>,
): ModelMetadata<P> {
  const catalog = PROVIDER_CATALOGS[providerId];
  return catalog.models[
    modelId as keyof typeof catalog.models
  ] as ModelMetadata<P>;
}

const FEATURED_METADATA = FEATURED_MODELS.map((entry) => {
  const metadata = getModelMetadata(entry.providerId, entry.modelId);
  if (!metadata) {
    throw new Error(
      `Model metadata not found: ${entry.providerId}/${entry.modelId}`,
    );
  }
  return {
    providerId: entry.providerId,
    modelId: entry.modelId,
    metadata,
  } as const;
});

// UI側で選択肢に使うモデル情報。
export const AVAILABLE_MODELS = FEATURED_METADATA.map(
  ({ providerId, modelId, metadata }) =>
    ({
      value: modelId,
      label: metadata.name ?? modelId,
      provider: providerId,
    }) satisfies AvailableModel,
) as readonly AvailableModel[];

// チャット未指定時の既定値は、UI の表示順から独立させる。
export const DEFAULT_MODEL_ID: ModelId = "claude-sonnet-5-5";

// 質問例生成・自動メモリ抽出は、チャットの既定値を変えても既存の Haiku 設定を維持する。
export const HELPER_MODEL_ID: ModelId = "claude-haiku-5-5";

// トークン上限をlookupできるようマップ化。
export const MODEL_CONTEXT_LIMITS = Object.fromEntries(
  FEATURED_METADATA.map(({ modelId, metadata }) => [
    modelId,
    metadata.limit?.context,
  ]),
) as Record<ModelId, number | undefined>;

export type ModelId = (typeof AVAILABLE_MODELS)[number]["value"];
type ModelAttachmentInputModality = "text" | "image" | "pdf";

const LEGACY_MODEL_ID_MAP: Record<string, ModelId> = {
  "claude-haiku-4-5-20251001": "claude-haiku-5-5",
  "claude-haiku-4-5": "claude-haiku-5-5",
  "claude-opus-4-5-20251101": "claude-opus-5-5",
  "claude-opus-4-6": "claude-opus-5-5",
  "claude-opus-4-7": "claude-opus-5-5",
  "claude-sonnet-4-5-20250929": "claude-sonnet-5-5",
  "claude-sonnet-4-6": "claude-sonnet-5-5",
  "claude-sonnet-5": "claude-sonnet-5-5",
  "gemini-3-flash-preview": "gemini-3.8-flash",
  "gemini-3-pro-preview": "gemini-3.1-pro-preview",
  "gpt-5.6-luna": "gpt-6-luna",
  "gpt-5.6-sol": "gpt-6-astra",
  "gpt-5.6-terra": "gpt-6-sol",
};

export function isValidModelId(modelId: string): modelId is ModelId {
  return AVAILABLE_MODELS.some((m) => m.value === modelId);
}

export function normalizeModelId(modelId: string | null | undefined) {
  if (!modelId) {
    return null;
  }
  const migratedModelId = LEGACY_MODEL_ID_MAP[modelId] ?? modelId;
  return isValidModelId(migratedModelId) ? migratedModelId : null;
}

// プロバイダごとのLanguageModel生成関数。
const providerFactories = {
  [anthropicCatalog.id]: (modelId: string) => anthropic(modelId),
  [googleCatalog.id]: (modelId: string) => google(modelId),
  [openAICatalog.id]: (modelId: string) => openai(modelId),
} as const satisfies Partial<
  Record<RegisteredProviderId, (modelId: string) => LanguageModel>
>;

export function getLanguageModel(modelId: ModelId) {
  const model = AVAILABLE_MODELS.find((m) => m.value === modelId);
  if (!model?.provider) {
    throw new Error(`Unknown model: ${modelId}`);
  }

  const factory = providerFactories[model.provider];
  if (!factory) {
    throw new Error(`Unknown provider: ${model.provider}`);
  }

  return factory(model.value) satisfies LanguageModel;
}

function getFeaturedModelMetadata(modelId: ModelId) {
  const model = FEATURED_METADATA.find((entry) => entry.modelId === modelId);
  if (!model) {
    throw new Error(`Unknown model: ${modelId}`);
  }

  return model.metadata;
}

export type ModelCapabilityKey = "reasoning" | "temperature";

export function supportsModelCapability(
  modelId: ModelId,
  capability: ModelCapabilityKey,
): boolean {
  const metadata = getFeaturedModelMetadata(modelId);
  if (capability === "temperature") return metadata.temperature;
  return "reasoning" in metadata && metadata.reasoning === true;
}

function mediaTypeToAttachmentInputModality(
  mediaType: string,
): ModelAttachmentInputModality | null {
  if (!isSupportedChatAttachmentMediaType(mediaType)) return null;
  if (
    CHAT_TEXT_BASED_ATTACHMENT_MEDIA_TYPES.some(
      (supportedMediaType) => supportedMediaType === mediaType,
    )
  ) {
    return "text";
  }
  if (mediaType === "application/pdf") return "pdf";
  if (mediaType.startsWith("image/")) return "image";
  return null;
}

export function supportsModelAttachment(
  modelId: ModelId,
  mediaType: string,
): boolean {
  const metadata = getFeaturedModelMetadata(modelId);
  const modality = mediaTypeToAttachmentInputModality(mediaType);
  if (!modality) return false;

  return Boolean(
    metadata.attachment && metadata.modalities?.input?.includes(modality),
  );
}

/**
 * Anthropic provider のモデルかどうかを判定する（Amethyst-owned）。
 *
 * @remarks
 * prompt caching は `providerOptions.anthropic.cacheControl` を付与する Anthropic 専用の
 * runtime オプションであり、models.dev 由来の生成 catalog の capability ではない。
 * よって catalog に手で capability を足すのではなく、registry の provider で判定する。
 */
export function isAnthropicModel(modelId: ModelId): boolean {
  return (
    AVAILABLE_MODELS.find((model) => model.value === modelId)?.provider ===
    "anthropic"
  );
}

/**
 * Anthropic の adaptive thinking と summarized 表示を使えるか判定する。
 *
 * @remarks
 * models.dev の `reasoning_options` に effort 指定がある Claude は、AI SDK が
 * adaptive thinking として扱う。budget token 指定のみの旧世代モデルは、
 * top-level reasoning から provider に適した extended thinking へ変換させる。
 */
export function supportsAnthropicAdaptiveThinking(modelId: ModelId): boolean {
  if (!isAnthropicModel(modelId)) return false;

  const metadata = getFeaturedModelMetadata(modelId);
  if (
    !("reasoning_options" in metadata) ||
    !Array.isArray(metadata.reasoning_options)
  ) {
    return false;
  }

  return metadata.reasoning_options.some(
    (option: { type?: unknown }) => option.type === "effort",
  );
}
